import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, CalendarCheck } from "lucide-react";
import { WeekProofStrip } from "@/components/WeekProofStrip";
import { CheckinForm } from "@/components/CheckinForm";

const fmt = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" });

const CindrRule = () => (
  <section className="card concrete relative overflow-hidden" data-testid="cindr-rule">
    <div className="relative z-10">
      <p className="eyebrow" style={{ color: "#FF0055" }}>CINDR Rule</p>
      <p className="font-display font-black text-xl md:text-2xl mt-2 uppercase leading-tight">
        You are not starting over.
      </p>
      <p className="text-zinc-300 mt-2 text-sm md:text-base">You are adjusting, learning, and coming back stronger.</p>
      <p className="font-display font-black text-lg mt-4 uppercase" style={{ color: "#FF0055" }}>Reset. Rebuild. Keep going.</p>
    </div>
  </section>
);

const CheckinResult = ({ c }) => (
  <section className="space-y-4" data-testid="checkin-result">
    <div className="card" style={{ borderColor: "#FF0055" }}>
      <p className="eyebrow" style={{ color: "#FF0055" }}>Locked in · {fmt(c.week_start)} – {fmt(c.week_end)}</p>
      <p className="font-display font-black text-xl md:text-2xl mt-3 uppercase leading-tight">
        This week, I proved to myself that I can <span style={{ color: "#FF0055" }} data-testid="result-proved">{c.proved}</span>
      </p>
      <p className="eyebrow mt-6">One promise for next week</p>
      <p className="font-display font-bold text-lg mt-1 uppercase" data-testid="result-promise">{c.promise}</p>
      {c.need_more?.length > 0 && (
        <p className="text-xs text-zinc-400 mt-4 uppercase tracking-widest">More of: {c.need_more.join(" · ")}</p>
      )}
    </div>
    <div className="card flex gap-4" data-testid="coach-reply">
      <div className="w-10 h-10 flex items-center justify-center shrink-0" style={{ background: "#FF0055" }}>
        <Sparkles size={16} color="white" />
      </div>
      <div>
        <p className="eyebrow" style={{ color: "#FF0055" }}>CINDR Coach</p>
        <p className="text-sm md:text-base text-zinc-200 mt-2 leading-relaxed whitespace-pre-line">{c.coach_reply}</p>
      </div>
    </div>
  </section>
);

export default function CheckinPage() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    const [s, h] = await Promise.all([api.get("/checkin/summary"), api.get("/checkin")]);
    setData(s.data);
    setHistory(h.data.checkins);
  };
  useEffect(() => { load(); }, []);

  const submit = async (payload) => {
    setSubmitting(true);
    setError("");
    try {
      const { data: saved } = await api.post("/checkin", payload);
      setData((d) => ({ ...d, existing: saved }));
      setHistory((h) => [saved, ...h.filter((x) => x.week_start !== saved.week_start)]);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(e.response?.data?.detail || "Could not save. Try again.");
    }
    setSubmitting(false);
  };

  if (!data) return <div className="p-10 text-zinc-500">Loading…</div>;
  const { summary, existing } = data;
  const past = history.filter((c) => c.week_start !== summary.week_start);

  return (
    <div className="fade-up space-y-10" data-testid="checkin-page">
      <header>
        <p className="eyebrow" style={{ color: "#FF0055" }}>
          Week of {fmt(summary.week_start)} – {fmt(summary.week_end)}
        </p>
        <h1 className="font-display font-black text-3xl md:text-5xl leading-[0.95] mt-2">
          CINDR // SUNDAY <span style={{ color: "#FF0055" }}>CHECK-IN</span>
        </h1>
        <p className="text-zinc-300 mt-4 max-w-xl text-sm md:text-base leading-relaxed">
          No fluff. No fake “perfect week.”<br />Tell the truth and use it.
        </p>
      </header>

      <section>
        <p className="eyebrow mb-4">This week’s proof</p>
        <WeekProofStrip summary={summary} />
      </section>

      {existing ? <CheckinResult c={existing} /> : (
        <>
          <CheckinForm options={data.need_more_options} onSubmit={submit} submitting={submitting} />
          {error && <p className="text-sm" style={{ color: "#FF0055" }} data-testid="checkin-error">{error}</p>}
        </>
      )}

      <CindrRule />

      {past.length > 0 && (
        <section data-testid="checkin-history">
          <p className="eyebrow">The trail</p>
          <h2 className="font-display font-black text-2xl md:text-3xl mt-1">PAST CHECK-INS</h2>
          <div className="space-y-3 mt-5 stagger">
            {past.map((c) => (
              <div key={c.id} className="card" data-testid={`history-${c.week_start}`}>
                <p className="eyebrow flex items-center gap-1.5"><CalendarCheck size={11} /> {fmt(c.week_start)} – {fmt(c.week_end)}</p>
                <p className="font-display font-bold mt-2 uppercase text-sm md:text-base">
                  I proved I can <span style={{ color: "#FF0055" }}>{c.proved}</span>
                </p>
                <p className="text-sm text-zinc-400 mt-1">Promise: {c.promise}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
